import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Sparkles, Check, X, Zap, Target, Award, HelpCircle, ChevronDown, ChevronUp, ArrowRight, CircleDot, Circle, AlertTriangle, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// ─── Litmus Test Visual Pipeline ───────────────────────────────────
const LITMUS_TESTS = [
  {
    id: 1,
    label: "Metric Movement",
    shortQ: "Does it change a KR number?",
    directAnswer: "Yes → DIRECT",
    icon: "📊",
  },
  {
    id: 2,
    label: "Output Consumed",
    shortQ: "Is output used by another task first?",
    indirectAnswer: "Yes → INDIRECT",
    icon: "🔗",
  },
  {
    id: 3,
    label: "Final Deliverable",
    shortQ: "Is this the last mile?",
    directAnswer: "Yes → DIRECT",
    icon: "🏁",
  },
  {
    id: 4,
    label: "Standalone Value",
    shortQ: "Would this alone move the KR?",
    directAnswer: "Yes → DIRECT",
    indirectAnswer: "No → INDIRECT",
    icon: "🎯",
  },
];

function LitmusTestPipeline({ activeTest, category }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 mb-2">
        <ShieldCheck className="w-3.5 h-3.5 text-indigo-400" />
        <span className="text-xs font-semibold text-indigo-300 uppercase tracking-wider">Litmus Tests Applied</span>
      </div>
      <div className="flex items-stretch gap-0">
        {LITMUS_TESTS.map((test, idx) => {
          const isActive = test.id === activeTest;
          const isPassed = test.id < activeTest;
          const isSkipped = test.id > activeTest;
          
          // Determine the result color based on category for the active test
          const resultColor = isActive
            ? category === 'direct'
              ? 'border-emerald-500/60 bg-emerald-500/10'
              : 'border-amber-500/60 bg-amber-500/10'
            : '';
          
          const textColor = isActive
            ? category === 'direct'
              ? 'text-emerald-400'
              : 'text-amber-400'
            : '';

          return (
            <React.Fragment key={test.id}>
              {/* Test Node */}
              <div
                className={`
                  relative flex-1 rounded-lg border px-2.5 py-2 transition-all
                  ${isActive 
                    ? `${resultColor} shadow-sm` 
                    : isPassed 
                      ? 'border-slate-700/50 bg-slate-800/30 opacity-50'
                      : 'border-slate-800/30 bg-slate-900/20 opacity-30'
                  }
                `}
              >
                {/* Test number + icon */}
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-sm">{test.icon}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${isActive ? textColor : 'text-slate-500'}`}>
                    Test {test.id}
                  </span>
                  {isActive && (
                    <span className={`ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                      category === 'direct' 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      MATCH
                    </span>
                  )}
                  {isPassed && (
                    <span className="ml-auto text-[9px] text-slate-600 font-medium">PASS</span>
                  )}
                </div>
                
                {/* Test name */}
                <div className={`text-xs font-semibold leading-tight ${isActive ? 'text-slate-200' : 'text-slate-500'}`}>
                  {test.label}
                </div>
                
                {/* Short question */}
                <div className={`text-[10px] mt-0.5 leading-snug ${isActive ? 'text-slate-400' : 'text-slate-600'}`}>
                  {test.shortQ}
                </div>
              </div>

              {/* Arrow between tests */}
              {idx < LITMUS_TESTS.length - 1 && (
                <div className="flex items-center px-0.5 self-center">
                  <ArrowRight className={`w-3 h-3 ${isPassed ? 'text-slate-600' : 'text-slate-800'}`} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ─── Confidence Indicator ──────────────────────────────────────────
function ConfidenceIndicator({ confidence }) {
  const config = {
    high: { 
      color: 'text-emerald-400', 
      bg: 'bg-emerald-500/10 border-emerald-500/30',
      icon: <ShieldCheck className="w-3 h-3" />,
      label: 'High Confidence'
    },
    medium: { 
      color: 'text-amber-400', 
      bg: 'bg-amber-500/10 border-amber-500/30',
      icon: <ShieldAlert className="w-3 h-3" />,
      label: 'Medium — Review Suggested'
    },
    low: { 
      color: 'text-red-400', 
      bg: 'bg-red-500/10 border-red-500/30',
      icon: <AlertTriangle className="w-3 h-3" />,
      label: 'Low — Human Review Needed'
    },
  };
  
  const c = config[confidence] || config.medium;
  
  return (
    <div className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-1 rounded-full border ${c.bg} ${c.color}`}>
      {c.icon}
      {c.label}
    </div>
  );
}

// ─── KR Match Card ─────────────────────────────────────────────────
function KRMatchCard({ matchedKR, category }) {
  if (!matchedKR || matchedKR === 'none') return null;
  
  return (
    <div className={`flex items-start gap-2 p-2 rounded-md border ${
      category === 'direct' 
        ? 'border-emerald-500/20 bg-emerald-500/5' 
        : 'border-amber-500/20 bg-amber-500/5'
    }`}>
      <Target className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
        category === 'direct' ? 'text-emerald-400' : 'text-amber-400'
      }`} />
      <div>
        <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">
          Matched Key Result
        </div>
        <div className="text-xs text-slate-300 leading-snug">{matchedKR}</div>
      </div>
    </div>
  );
}

// ─── Points Display ────────────────────────────────────────────────
function PointsDisplay({ category, xpValue, epValue, skillRewards }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Main points badge — large and prominent */}
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
        category === 'direct'
          ? 'border-emerald-500/40 bg-emerald-500/10'
          : 'border-amber-500/40 bg-amber-500/10'
      }`}>
        {category === 'direct' ? (
          <Target className="w-4 h-4 text-emerald-400" />
        ) : (
          <Zap className="w-4 h-4 text-amber-400" />
        )}
        <span className={`text-lg font-bold tabular-nums ${
          category === 'direct' ? 'text-emerald-400' : 'text-amber-400'
        }`}>
          +{category === 'direct' ? xpValue : epValue}
        </span>
        <span className={`text-xs font-semibold ${
          category === 'direct' ? 'text-emerald-500/70' : 'text-amber-500/70'
        }`}>
          {category === 'direct' ? 'XP' : 'EP'}
        </span>
      </div>

      {/* Skill badges */}
      {skillRewards && skillRewards.length > 0 && (
        <div className="flex gap-1.5 flex-wrap">
          {skillRewards.map((skill, idx) => (
            <div key={idx} className="flex items-center gap-1 px-2 py-1 rounded-md border border-purple-500/25 bg-purple-500/5">
              <Award className="w-3 h-3 text-purple-400" />
              <span className="text-[11px] font-medium text-purple-300">{skill.skill_name}</span>
              <span className="text-[10px] font-bold text-purple-500">+{skill.sp_amount}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Linked Task Hint ──────────────────────────────────────────────
function LinkedTaskHint({ hint, category }) {
  if (!hint || category !== 'indirect') return null;
  
  return (
    <div className="flex items-start gap-2 p-2 rounded-md border border-amber-500/15 bg-amber-500/5">
      <span className="text-sm mt-px">💡</span>
      <div>
        <div className="text-[10px] font-semibold text-amber-400/70 uppercase tracking-wider mb-0.5">
          Enables Direct Task
        </div>
        <div className="text-xs text-amber-200/80 leading-snug">{hint}</div>
      </div>
    </div>
  );
}

// ─── Main Panel ────────────────────────────────────────────────────
export default function TaskCategorizationPanel({ 
  taskDescription, 
  keyResultFocus, 
  activityId,
  onCategorize,
  initialValues = null
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(initialValues);
  const [isApproved, setIsApproved] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [showFullReasoning, setShowFullReasoning] = useState(false);
  
  // Override values
  const [category, setCategory] = useState(initialValues?.category || 'direct');
  const [xpValue, setXpValue] = useState(initialValues?.xp_value || 0);
  const [epValue, setEpValue] = useState(initialValues?.ep_value || 0);
  const [skillRewards, setSkillRewards] = useState(initialValues?.skill_rewards || []);
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillSp, setNewSkillSp] = useState(1);

  const handleAskBot = async () => {
    if (!taskDescription || !activityId) {
      alert('Please enter a task description and select an activity first.');
      return;
    }
    
    setIsLoading(true);
    setIsApproved(false);
    setOverrideMode(false);
    setShowFullReasoning(false);
    
    try {
      const response = await base44.functions.invoke('categorizeTaskAndSuggestPoints', {
        taskDescription,
        keyResultFocus,
        activityId
      });
      
      if (response.data.success) {
        const suggestionData = response.data.suggestion;
        setSuggestion({
          ...suggestionData,
          context: response.data.context
        });
        setCategory(suggestionData.category);
        setXpValue(suggestionData.xp_value || 0);
        setEpValue(suggestionData.ep_value || 0);
        setSkillRewards(suggestionData.skill_rewards || []);
      }
    } catch (error) {
      console.error('Failed to categorize task:', error);
      alert('Failed to get bot suggestion. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = () => {
    setIsApproved(true);
    onCategorize({
      category,
      xp_value: category === 'direct' ? xpValue : 0,
      ep_value: category === 'indirect' ? epValue : 0,
      skill_rewards: skillRewards,
      bot_suggested_category: suggestion?.category,
      bot_suggested_xp: suggestion?.xp_value,
      bot_suggested_ep: suggestion?.ep_value,
      bot_suggested_skills: suggestion?.skill_rewards,
      bot_suggestion_approved: !overrideMode,
      litmus_test_used: suggestion?.litmus_test_used,
      matched_key_result: suggestion?.matched_key_result,
      confidence: suggestion?.confidence
    });
  };

  const addSkillReward = () => {
    if (newSkillName.trim() && newSkillSp > 0) {
      setSkillRewards([...skillRewards, { skill_name: newSkillName.trim(), sp_amount: newSkillSp }]);
      setNewSkillName('');
      setNewSkillSp(1);
    }
  };

  const removeSkillReward = (index) => {
    setSkillRewards(skillRewards.filter((_, i) => i !== index));
  };

  return (
    <div className="border border-indigo-500/30 rounded-lg bg-indigo-500/5 p-4 space-y-4">
      {/* ─── Header ─── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-400" />
          <span className="text-sm font-semibold text-slate-200">Gamification Points</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="w-3 h-3 text-slate-500" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  <strong>Direct Tasks</strong> earn XP immediately — they move a Key Result metric.<br/>
                  <strong>Indirect Tasks</strong> earn EP that converts to XP when the enabled direct task completes.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {!suggestion && (
          <Button
            size="sm"
            onClick={handleAskBot}
            disabled={isLoading || !taskDescription || !activityId}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-3 h-3 mr-1" />
                Ask Compass_REF_Bot
              </>
            )}
          </Button>
        )}
      </div>

      {/* ─── Bot Suggestion Display ─── */}
      {suggestion && !overrideMode && (
        <div className="space-y-3">

          {/* ── Category + Confidence row ── */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Badge className={`text-sm px-3 py-1 ${
              suggestion.category === 'direct' 
                ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
            }`}>
              {suggestion.category === 'direct' ? (
                <><Target className="w-3.5 h-3.5 mr-1.5" /> Direct Task</>
              ) : (
                <><Zap className="w-3.5 h-3.5 mr-1.5" /> Indirect Task</>
              )}
            </Badge>
            
            {suggestion.confidence && (
              <ConfidenceIndicator confidence={suggestion.confidence} />
            )}
          </div>

          {/* ── Litmus Test Pipeline ── */}
          {suggestion.litmus_test_used && (
            <LitmusTestPipeline 
              activeTest={suggestion.litmus_test_used} 
              category={suggestion.category} 
            />
          )}

          {/* ── KR Match ── */}
          <KRMatchCard 
            matchedKR={suggestion.matched_key_result} 
            category={suggestion.category} 
          />

          {/* ── Points + Skills ── */}
          <PointsDisplay
            category={suggestion.category}
            xpValue={suggestion.xp_value}
            epValue={suggestion.ep_value}
            skillRewards={suggestion.skill_rewards}
          />

          {/* ── Linked Task Hint (indirect only) ── */}
          <LinkedTaskHint 
            hint={suggestion.suggested_linked_direct_task_hint} 
            category={suggestion.category} 
          />

          {/* ── Budget Warning ── */}
          {suggestion.context && suggestion.context.remaining_xp_budget <= 0 && suggestion.category === 'direct' && (
            <div className="flex items-center gap-2 p-2 rounded-md border border-red-500/20 bg-red-500/5">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
              <span className="text-xs text-red-300">
                XP budget is depleted for this activity. Points suggested but may need reallocation.
              </span>
            </div>
          )}

          {/* ── Expandable Reasoning ── */}
          {suggestion.reasoning && (
            <button 
              onClick={() => setShowFullReasoning(!showFullReasoning)}
              className="flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-slate-400 transition-colors w-full text-left"
            >
              {showFullReasoning ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              <span className="font-medium">
                {showFullReasoning ? 'Hide' : 'Show'} full reasoning
              </span>
            </button>
          )}
          {showFullReasoning && suggestion.reasoning && (
            <div className="text-xs text-slate-400 bg-slate-800/30 p-2.5 rounded border border-slate-700/50 leading-relaxed">
              {suggestion.reasoning}
            </div>
          )}

          {/* ── Action Buttons ── */}
          {!isApproved && (
            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                onClick={handleApprove}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
              >
                <Check className="w-3 h-3 mr-1" />
                Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setOverrideMode(true)}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Override
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleAskBot}
                className="text-slate-400 hover:text-slate-200"
              >
                Re-analyze
              </Button>
            </div>
          )}

          {isApproved && (
            <div className="flex items-center gap-2 text-emerald-400 text-sm">
              <Check className="w-4 h-4" />
              Points configuration saved!
            </div>
          )}
        </div>
      )}

      {/* ─── Override/Manual Mode ─── */}
      {(overrideMode || (!suggestion && initialValues)) && (
        <div className="space-y-3 pt-2 border-t border-slate-700/50">
          <div className="text-xs text-slate-400 mb-2">Manual Configuration</div>
          
          {/* Category Selection */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 w-20">Category:</span>
            <Select value={category} onValueChange={(v) => {
              setCategory(v);
              if (v === 'direct') setEpValue(0);
              else setXpValue(0);
            }}>
              <SelectTrigger className="w-40 bg-slate-950 border-slate-700 text-slate-100 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="direct">Direct (XP)</SelectItem>
                <SelectItem value="indirect">Indirect (EP)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Points Input */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400 w-20">
              {category === 'direct' ? 'XP Value:' : 'EP Value:'}
            </span>
            <Input data-testid="game-taskcategorizationpanel-input"
              type="number"
              min={0}
              max={category === 'direct' ? 100 : 50}
              value={category === 'direct' ? xpValue : epValue}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  if (category === 'direct') setXpValue(0);
                  else setEpValue(0);
                  return;
                }
                const val = parseInt(raw, 10);
                if (!isNaN(val)) {
                  if (category === 'direct') setXpValue(val);
                  else setEpValue(val);
                }
              }}
              className="w-20 bg-slate-950 border-slate-700 text-slate-100 h-8 text-xs"
            />
            <span className="text-xs text-slate-500">
              {category === 'direct' ? '(5-100 typical)' : '(5-50 typical)'}
            </span>
          </div>

          {/* Skill Rewards */}
          <div className="space-y-2">
            <span className="text-xs text-slate-400">Skill Rewards:</span>
            <div className="flex flex-wrap gap-1 min-h-[28px]">
              {skillRewards.map((skill, idx) => (
                <Badge key={idx} variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/30">
                  {skill.skill_name} +{skill.sp_amount} SP
                  <button onClick={() => removeSkillReward(idx)} className="ml-1 hover:text-red-400">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input data-testid="game-taskcategorizationpanel-input-1"
                placeholder="Skill name..."
                value={newSkillName}
                onChange={(e) => setNewSkillName(e.target.value)}
                className="flex-1 bg-slate-950 border-slate-700 text-slate-100 h-8 text-xs"
              />
              <Input data-testid="game-taskcategorizationpanel-input-2"
                type="number"
                min={1}
                max={10}
                value={newSkillSp}
                onChange={(e) => setNewSkillSp(parseInt(e.target.value) || 1)}
                className="w-16 bg-slate-950 border-slate-700 text-slate-100 h-8 text-xs"
              />
              <Button size="sm" onClick={addSkillReward} className="h-8 px-2">
                +
              </Button>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              onClick={handleApprove}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
            >
              <Check className="w-3 h-3 mr-1" />
              Save Points Configuration
            </Button>
            {suggestion && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setOverrideMode(false);
                  setCategory(suggestion.category);
                  setXpValue(suggestion.xp_value || 0);
                  setEpValue(suggestion.ep_value || 0);
                  setSkillRewards(suggestion.skill_rewards || []);
                }}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Back to Suggestion
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ─── Empty state ─── */}
      {!suggestion && !initialValues && !isLoading && (
        <div className="text-xs text-slate-500 text-center py-2">
          Click "Ask Compass_REF_Bot" to get a suggested categorization and point values based on your task description.
        </div>
      )}
    </div>
  );
}